import type { SerializedEditorState } from 'lexical';

import StoreORM from 'js/src/data/store-orm';
import { createEditorFromEditorState } from 'js/src/data/transcript';
import type { LexicalEditorsDataType, SerializedTranscript } from 'js/src/data/transcript';
import type { RootState } from 'js/src/libs/RootState';
import type { GetRootState } from 'js/src/luminary/luminary-redux-connect-types';

/** The saved transcripts a fetched video ships, keyed by scene-group id (video.transcripts). */
type SavedTranscripts = Record<string, Partial<SerializedTranscript> | SerializedEditorState>;

/**
 * A reference video is loaded into an unmounted Luminary, so the session store has no transcript
 * editors for it — which means its pauses (SilenceNodes, read from the editor) can't be seen.
 *
 * Build headless Lexical editors from the video's saved transcript states (the same
 * createEditorFromEditorState the editor hydrates with) and overlay them onto the session state's
 * lexical_editors slice, producing a getState the capture pipeline can run. This makes a reference
 * video's transcript render through the exact same code as get_video_state — per-scene `covers:`
 * slices with pauses — rather than a separate, lesser view.
 *
 * The session state is spread, never mutated: StoreORM's Immutable persistent ops leave the live
 * lexical_editors untouched, and everything else the capture reads (notably the templates slice
 * that isSceneAnOverlay needs to classify overlays) is preserved as-is from the session state.
 *
 * Returns null when there are no transcripts to build editors from, so the caller can fall back to
 * the storeless description (structure only) rather than capture with an empty editor set.
 */
export function buildReferenceStateGetter(
    sessionState: RootState,
    transcripts: SavedTranscripts | undefined,
): GetRootState | null {
    if (!transcripts) {
        return null;
    }

    const editors: LexicalEditorsDataType[] = [];
    for (const sceneGroupId of Object.keys(transcripts)) {
        const transcript = transcripts[sceneGroupId] as Partial<SerializedTranscript>;
        // A saved transcript is either a SerializedTranscript (carrying .editorState) or the raw
        // editor state itself — the same two shapes hydrateTranscripts accepts.
        const editorState = (transcript?.editorState ?? transcripts[sceneGroupId]) as
            | SerializedEditorState
            | undefined;
        if (!editorState) {
            continue;
        }
        editors.push({
            editor: createEditorFromEditorState(editorState),
            sceneGroupId,
            lexicalControlsItsOwnState: false,
        });
    }

    if (editors.length === 0) {
        return null;
    }

    const orm = new StoreORM<LexicalEditorsDataType, string>(
        sessionState.transcript.lexical_editors,
    );
    orm.delete(orm.getAllIds());
    editors.forEach((editor) => orm.pushJSItem(editor, 'sceneGroupId'));

    const referenceState: RootState = {
        ...sessionState,
        transcript: { ...sessionState.transcript, lexical_editors: orm.data },
    };
    return () => referenceState;
}
